<script setup lang="ts">
import {
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuPortal,
  DropdownMenuRoot,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from 'reka-ui'
import Icon from './Icon.vue'
import type { MenuGroup, MenuItem } from './menu'

withDefaults(defineProps<{
  label: string
  groups: MenuGroup[]
  side?: 'top' | 'right' | 'bottom' | 'left'
  align?: 'start' | 'center' | 'end'
  /** default = the user menu (fixed width, 32px items); compact = a row's context menu (content-wide, 28px items). */
  size?: 'default' | 'compact'
}>(), { side: 'bottom', align: 'start', size: 'default' })
const open = defineModel<boolean>('open', { default: false })
const emit = defineEmits<{ select: [item: MenuItem, event: Event] }>()
</script>

<template>
  <DropdownMenuRoot v-model:open="open" :modal="false">
    <DropdownMenuTrigger as-child><slot name="trigger" /></DropdownMenuTrigger>
    <DropdownMenuPortal>
      <DropdownMenuContent
        :aria-label="label" :side="side" :align="align" :side-offset="4" :collision-padding="8" loop
        class="z-50 max-h-[var(--reka-dropdown-menu-content-available-height)] max-w-[var(--reka-dropdown-menu-content-available-width)] overflow-y-auto rounded-lg border border-border bg-popover p-[var(--menu-inset)] text-foreground shadow-[var(--shadow-menu)] outline-none"
        :class="size === 'compact' ? 'w-max min-w-[var(--menu-width-compact)]' : 'w-[var(--menu-width)]'"
      >
        <template v-for="(group, index) in groups" :key="group.id">
          <DropdownMenuSeparator v-if="index" class="my-[var(--menu-inset)] h-px bg-border" />
          <DropdownMenuGroup>
            <DropdownMenuLabel v-if="group.label" class="px-[var(--menu-item-inset)] py-1 text-[length:var(--type-meta)] font-semibold text-muted-foreground">{{ group.label }}</DropdownMenuLabel>
            <DropdownMenuItem
              v-for="item in group.items" :key="item.id" :disabled="item.disabled" :text-value="item.label"
              class="flex cursor-pointer select-none items-center gap-2 text-xs outline-none data-[highlighted]:bg-sidebar-accent data-[disabled]:pointer-events-none data-[disabled]:opacity-50"
              :class="[item.destructive ? 'text-destructive' : 'text-foreground', size === 'compact' ? 'h-[var(--menu-item-height-compact)] rounded-[var(--radius-compact)] px-[var(--menu-item-inset-compact)]' : 'min-h-[var(--menu-item-height)] rounded-[var(--radius-control)] px-[var(--menu-item-inset)] py-1']"
              @select="emit('select', item, $event)"
            >
              <Icon v-if="item.icon" :name="item.icon" :size="size === 'compact' ? 12 : 13" class="shrink-0" :class="!item.destructive && 'text-muted-foreground'" />
              <span class="min-w-0 flex-1 truncate">{{ item.label }}</span>
              <Icon v-if="item.active" name="check" :size="12" class="shrink-0 text-primary" aria-label="Selected" />
            </DropdownMenuItem>
          </DropdownMenuGroup>
        </template>
        <slot />
        <div v-if="$slots.footer" class="mt-[var(--menu-inset)] border-t border-border px-[var(--menu-item-inset)] py-1.5 text-[length:var(--type-meta)] text-muted-foreground"><slot name="footer" /></div>
      </DropdownMenuContent>
    </DropdownMenuPortal>
  </DropdownMenuRoot>
</template>
